import {
  IcAddLinkIcon,
  IcCalendarIcon,
  IcCopyImage24Icon,
  IcDoneIcon,
  IcDownloadIcon,
  IcGoToMessageIcon,
  IcInfoIcon,
  IcLinkIcon,
  IcMessagesResendIcon,
  IcMessagesIcon,
  IcPublishIcon,
  IcQuote24Icon,
  IcReportIcon,
  IcSCopyIcon,
  IcSDeleteIcon,
  IcSEditIcon,
  IcSForwardIcon,
  IcSPinIcon,
  IcSReplyIcon,
  IcSUnpinIcon,
  IcStarSIcon,
  IcTranslateIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { getOriginalSizeImage } from '@gem/modules/image-resizer';
import { MenuItemStyle, IPopoverMenuItem, filterPopoverMenuItems } from '@gem/ui-kit';
import {
  CLIPBOARD_SUPPORTED,
  copyImageToClipboard,
  copyTextToClipboard,
  MessageContentType,
  findMessageContentNode,
  findMessageNodeById,
  platformInfo,
} from '@gem/utils';

import Tasks from 'APP/Tasks';
import { showConfirmWithCheckbox } from 'APP/Tasks/app/app';
import { MessageViewPageType } from 'APP/main/ChatView/Messages/Messages.types';
import { AddToSpaceCollectionPopup } from 'APP/main/PopupManager/Popups/AddToSpaceCollectionPopup/AddToSpaceCollectionPopup';
import { ForwardMessagePopup } from 'APP/main/PopupManager/Popups/ForwardMessagePopup/ForwardMessagePopup';
import { ForwardMessageQuotePopup } from 'APP/main/PopupManager/Popups/ForwardMessageQuotePopup/ForwardMessageQuotePopup';
import { ReactionsDetailedPopup } from 'APP/main/PopupManager/Popups/ReactionsDetailedPopup/ReactionsDetailedPopup';
import { ReportMessagePopup } from 'APP/main/PopupManager/Popups/ReportMessagePopup/ReportMessagePopup';
import { ScheduleSettingsPopup } from 'APP/main/PopupManager/Popups/ScheduleSettingsPopup/ScheduleSettingsPopup';
import { DeleteMessagesMode, PayloadType } from 'APP/model/message/messageModel.types';
import { Features } from 'APP/packages/features/Features';
import { RouterPage, RouterState } from 'APP/router/constants';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';
import { getMessageTextForCopy } from 'UTILS/message/message';

import styles from './ContextMenu.styles.m.css';
import { isTextSelected } from './ContextMenu.utils';

export interface IContextMenuPresenterParams {
  message: ChatMessage;
  messageViewPageType: MessageViewPageType;
  targetClick: HTMLElement | null;
}

interface IContextMenuPresenter {
  menuItems: IPopoverMenuItem[];
}

export const useContextMenuPresenter = ({
  message,
  messageViewPageType,
  targetClick,
}: IContextMenuPresenterParams): IContextMenuPresenter => {
  const navigateTo = useNavigateTo();
  const { t } = useTranslation();

  const isLink = targetClick?.tagName.toLowerCase() === 'a';

  const startForward = (): void => {
    Tasks.app.openPopup(ForwardMessagePopup, {
      params: { groupId: message.groupId, messageIds: [message.id] },
    });
  };

  const canCopyImage = (): boolean => {
    if (platformInfo.isSafari) {
      // Safari limitation:
      // navigator.clipboard работает только при прямом действии пользователя (click/pointerdown).
      // См. задачу GEM-33000.
      return false;
    }

    if (!CLIPBOARD_SUPPORTED || message.group?.isSecretGroup) {
      return false;
    }

    return (
      targetClick?.tagName.toLowerCase() === 'img' ||
      message.payload.payloadType === PayloadType.Image
    );
  };

  const isMediaMessage = (): boolean => {
    if (
      ![PayloadType.Album, PayloadType.Video, PayloadType.Image].includes(
        message.payload.payloadType
      )
    ) {
      return false;
    }
    return !!message.payload.comment;
  };

  const copySelectedText = async (): Promise<void> => {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    await copyTextToClipboard(selection.toString());
  };

  const quoteSelectedText = (): void => {
    const selection = window.getSelection();
    const text = selection?.toString();

    if (!text) {
      return;
    }

    Tasks.messaging.startQuote(message, text);
  };

  const quoteSelectedTextAndForward = (): void => {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    Tasks.app.openPopup(ForwardMessageQuotePopup, {
      params: { message, text: selection.toString() },
    });
  };

  const copyMessageText = async (): Promise<void> => {
    await copyTextToClipboard(getMessageTextForCopy(message, false));
  };

  const copyImage = async (): Promise<void> => {
    const src =
      targetClick instanceof HTMLImageElement
        ? targetClick.src
        : message.payload.payloadType === PayloadType.Image &&
          (message.payload.url || message.payload.localPreview);

    if (!src) {
      return;
    }

    const imageUrl = getOriginalSizeImage(src);

    const result = await copyImageToClipboard(imageUrl);
    if (result) {
      Tasks.app.showToast(t('common_copy_this_media_success'));
    }
  };

  const copyLink = async (): Promise<void> => {
    const href = (targetClick as HTMLLinkElement).href;
    await copyTextToClipboard(href);
  };

  const messageInfoPopup = (): void => {
    Tasks.app.openPopup(ReactionsDetailedPopup, {
      params: { groupId: message.groupId, messageId: message.id },
    });
  };

  const pinUnpin = async (): Promise<void> => {
    const canPinUnpin = await Tasks.app.showConfirm({
      text: message.isPinned ? t('unpin_message_confirmation') : t('pin_message_confirmation'),
    });

    if (canPinUnpin) {
      Tasks.messaging.pinUnpinMessage(message);
    }
  };

  const copyDeepLink = (): void => {
    Tasks.messaging.copyDeepLink(message);
  };

  const leaveComment = async (): Promise<void> => {
    const group = await Tasks.thread.getOrCreateThread(message);
    if (group) {
      navigateTo({
        to: group.routePath,
        state: {
          [RouterState.hasGoBack]: true,
        },
      });
    }
  };

  const selectMessage = (): void => {
    if (message.group?.id) {
      Tasks.messaging.selectMessage(message.group.id, message.id);
    }
  };

  const editMessage = (): void => {
    if (!message.group?.canSendMessages) {
      Tasks.app.showToast(t('moderation_disable_send_message'));
      return;
    }

    if (message.payload.payloadType === PayloadType.Article) {
      navigateTo({
        to: `/${RouterPage.Channels}/${message.groupId}/article/${message.id}`,
      });

      return;
    }

    Tasks.messaging.startEditMessage({
      groupId: message.groupId,
      editMessageId: message.id,
    });
  };

  const deleteMessage = async (): Promise<void> => {
    let canDelete = false;
    let deleteMode = DeleteMessagesMode.All;

    if (message.canBeDelete && message.canBeDeleteForMe) {
      const { isConfirmed, isChecked } = await showConfirmWithCheckbox({
        text: t('delete_message'),
        checkBoxText: t('delete_for_me'),
      });

      canDelete = isConfirmed;
      deleteMode = isChecked ? DeleteMessagesMode.Me : DeleteMessagesMode.All;
    }

    if (message.canBeDelete && !message.canBeDeleteForMe) {
      canDelete = await Tasks.app.showConfirm({ text: t('delete_message') });
    }

    if (!message.canBeDelete && message.canBeDeleteForMe) {
      canDelete = await Tasks.app.showConfirm({ text: t('common_delete_for_me_alert') });
      deleteMode = DeleteMessagesMode.Me;
    }

    if (!canDelete || !message.group) {
      return;
    }

    message.delete(deleteMode);
  };

  const report = (): void => {
    Tasks.app.openPopup(ReportMessagePopup, {
      params: { groupId: message.groupId, messageId: message.id },
    });
  };

  const replyMessage = (): void => {
    Tasks.messaging.startReplyMessage(message);
  };

  const addToSpaceCollection = (): void => {
    Tasks.app.openPopup(AddToSpaceCollectionPopup, {
      params: { groupId: message.groupId, messageId: message.id },
    });
  };

  const downloadMessage = (): void => {
    Tasks.downloadMessageFile({
      payloadId: message.id,
      groupId: message.groupId,
      messageId: message.id,
    });
  };

  const sendNowMessage = (): void => {
    Tasks.messaging.sendNowScheduledMessages(message.groupId, [message]);
  };

  const scheduleMessage = async (): Promise<void> => {
    if (Entities.GroupsStore.activeGroupId) {
      Tasks.messaging.clearInputPanel(Entities.GroupsStore.activeGroupId, false);
    }

    Tasks.app.openPopup(ScheduleSettingsPopup, {
      params: {
        date: new Date(message.serverTime),
        onSubmit: async (date: Date): Promise<void> => {
          await Tasks.messaging.editScheduledMessage({
            message,
            date: new Date(date.getTime()),
          });
        },
      },
    });
  };

  const goToPinnedMessage = (): void => {
    if (message.group) {
      message.group.pinnedMessages.setPrevMessageIdOnFocusBased(message.id);

      Tasks.messaging.focusMessage({
        groupId: message.groupId,
        messageId: message.id,
        isAnimated: false,
      });
      navigateTo({ to: message.group.routePath });
    }
  };

  const isAllPinsPage = messageViewPageType === MessageViewPageType.AllPins;

  const menuItems = [
    {
      key: 'goToMessage',
      isVisible: message.isPinned && isAllPinsPage,
      title: t('common_go_to_message'),
      onClick: goToPinnedMessage,
      icon: IcGoToMessageIcon,
    },
    {
      key: 'sendnow',
      isVisible: message.canBeSendNow,
      title: t('common_send_now'),
      onClick: sendNowMessage,
      icon: IcPublishIcon,
    },
    {
      key: 'schedule',
      isVisible: message.canBeSchedule,
      title: t('common_reschedule'),
      onClick: scheduleMessage,
      icon: IcCalendarIcon,
    },
    {
      key: 'resend',
      isVisible: message.canBeResend,
      title: t('message_resend'),
      onClick: message.resend,
      icon: IcMessagesResendIcon,
    },
    {
      key: 'reschedule',
      isVisible: message.canBeReschedule,
      title: t('message_resend'),
      onClick: message.reschedule,
      icon: IcMessagesResendIcon,
    },
    {
      key: 'copy',
      isVisible: message.canBeCopied,
      title: t(isMediaMessage() ? 'common_copy_text' : 'message_copy'),
      onClick: copyMessageText,
      icon: IcSCopyIcon,
    },
    {
      key: 'copy_selected',
      isVisible:
        !message.group?.isSecretGroup &&
        isTextSelected({ targetNode: targetClick || findMessageNodeById(message.id) }),
      title: t('common_copy_selected_text'),
      onClick: copySelectedText,
      icon: IcSCopyIcon,
    },
    {
      key: 'copy_link',
      isVisible: isLink,
      title: t('common_copy_link'),
      onClick: copyLink,
      icon: IcAddLinkIcon,
    },
    {
      key: 'copy_media',
      isVisible: canCopyImage(),
      title: t('common_copy_this_media'),
      onClick: copyImage,
      icon: IcCopyImage24Icon,
    },
    {
      key: 'reply',
      isVisible: message.canBeReplied && !isAllPinsPage,
      title: t('message_reply'),
      onClick: replyMessage,
      icon: IcSReplyIcon,
      iconClassName: styles.icon,
    },
    {
      key: 'add-to-space-collection',
      isVisible: message.canAddToSpaceCollection,
      title: t('space_add_to_collection'),
      onClick: addToSpaceCollection,
      icon: IcStarSIcon,
    },
    {
      key: 'edit',
      isVisible: message.canBeEdit && !isAllPinsPage,
      title: t('message_edit'),
      onClick: editMessage,
      icon: IcSEditIcon,
    },
    {
      key: 'pinned',
      isVisible: message.canBePinned,
      title: message.isPinned ? t('common_unpin') : t('common_pin'),
      onClick: pinUnpin,
      icon: message.isPinned ? IcSUnpinIcon : IcSPinIcon,
    },
    {
      key: 'forward',
      isVisible: message.canBeForward,
      title: t('message_forward'),
      onClick: startForward,
      icon: IcSForwardIcon,
      iconClassName: styles.icon,
    },
    {
      key: 'quote',
      title: t('chat_menu_quote_button'),
      icon: IcQuote24Icon,
      isVisible: isTextSelected({
        strict: true,
        targetNode: findMessageContentNode(message.id, MessageContentType.Text),
      }),
      items: [
        {
          key: 'quote_selected',
          isVisible: message.canBeQuoted && !isAllPinsPage,
          title: t('chat_menu_reply_with_quote_button'),
          onClick: quoteSelectedText,
          icon: IcSReplyIcon,
        },
        {
          key: 'quote_and_forward_selected',
          isVisible: message.canBeQuotedWithForward,
          title: t('chat_menu_quote_another_chat_button'),
          onClick: quoteSelectedTextAndForward,
          icon: IcSForwardIcon,
        },
      ],
    },
    {
      key: 'info',
      isVisible: message.canBeReactionsOrViews,
      title: t('group_chat_actions_sheet_info'),
      onClick: messageInfoPopup,
      icon: IcInfoIcon,
    },
    {
      key: 'translate',
      isVisible: message.canBeTranslated,
      title: t('common_translate'),
      onClick: message.translate,
      icon: IcTranslateIcon,
    },
    {
      key: 'deeplink',
      isVisible: message.canBeCreateLink,
      title: t('channel_deeplink_copy_link'),
      onClick: copyDeepLink,
      icon: IcLinkIcon,
    },
    {
      key: 'report',
      isVisible: Features.isReportsEnabled() && message.canBeReported,
      title: t('common_report'),
      onClick: report,
      icon: IcReportIcon,
    },
    {
      key: 'select',
      isVisible: message.canBeSelected,
      title: t('multiselect_context_menu_option'),
      onClick: selectMessage,
      icon: IcDoneIcon,
    },
    {
      key: 'comment',
      isVisible: message.canBeLeaveComment,
      title: t('comments_leave_comment'),
      onClick: leaveComment,
      icon: IcMessagesIcon,
    },
    {
      key: 'download',
      isVisible: message.canBeDownloaded,
      title: t('common_download'),
      onClick: downloadMessage,
      icon: IcDownloadIcon,
    },
    {
      key: 'delete',
      isVisible:
        !message.group?.isServiceMessages && (message.canBeDeleteForMe || message.canBeDelete),
      title: t('chat_list_delete_single_delete_button'),
      onClick: deleteMessage,
      icon: IcSDeleteIcon,
      style: MenuItemStyle.Danger,
    },
  ];

  return {
    menuItems: filterPopoverMenuItems(menuItems),
  };
};
